// Local helpers for favourites + offline caches.
import { putSavedRoute, deleteSavedRoute, getSavedRoute } from "./offlineDB";

const FAVS_KEY = "td_anon_favs";
const OFFLINE_KEY = "td_offline_locations";

// --- Favourites (anonymous) ---
export function getAnonFavs() {
  try { return JSON.parse(localStorage.getItem(FAVS_KEY) || "[]"); } catch { return []; }
}
export function setAnonFavs(ids) {
  localStorage.setItem(FAVS_KEY, JSON.stringify(Array.from(new Set(ids))));
  window.dispatchEvent(new Event("td-favs-changed"));
}
export function toggleAnonFav(id) {
  const cur = getAnonFavs();
  const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
  setAnonFavs(next);
  return next.includes(id);
}
export function clearAnonFavs() {
  localStorage.removeItem(FAVS_KEY);
  window.dispatchEvent(new Event("td-favs-changed"));
}

// --- Offline cache ---
// localStorage keeps the "sync-read" surface (size KB, list on Favourites) —
// IndexedDB is the source of truth for the full payload + Google deep links.
export function getOffline() {
  try { return JSON.parse(localStorage.getItem(OFFLINE_KEY) || "{}"); } catch { return {}; }
}
export function saveOffline(locationId, payload) {
  const all = getOffline();
  all[locationId] = { ...payload, _saved_at: new Date().toISOString() };
  localStorage.setItem(OFFLINE_KEY, JSON.stringify(all));
  // Mirror into IndexedDB with ToS-safe Google Maps deep links baked in.
  if (payload?.location) {
    putSavedRoute(payload.location, { weather: payload.weather }).catch((e) =>
      console.warn("IDB save failed", e)
    );
  }
  window.dispatchEvent(new Event("td-offline-changed"));
}
export function removeOffline(locationId) {
  const all = getOffline();
  delete all[locationId];
  localStorage.setItem(OFFLINE_KEY, JSON.stringify(all));
  deleteSavedRoute(locationId).catch(() => {});
  window.dispatchEvent(new Event("td-offline-changed"));
}
export function isOffline(locationId) {
  return Object.prototype.hasOwnProperty.call(getOffline(), locationId);
}
export function offlineSizeKB(locationId) {
  const all = getOffline();
  if (!all[locationId]) return 0;
  return Math.round(JSON.stringify(all[locationId]).length / 1024);
}
/** Async accessor that returns the FULL IndexedDB record (with deep links). */
export async function getOfflineRoute(locationId) {
  return getSavedRoute(locationId);
}

// --- GPX export ---
export function downloadGPX(location, polyline) {
  const pts = (polyline && polyline.length ? polyline : [[location.lat, location.lng]]);
  const trkpts = pts.map(([lat, lng]) => `      <trkpt lat="${lat}" lon="${lng}"></trkpt>`).join("\n");
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="TrailDog" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${escapeXml(location.name)}</name>
    <desc>${escapeXml(location.region || "United Kingdom")}</desc>
    <time>${new Date().toISOString()}</time>
  </metadata>
  <trk>
    <name>${escapeXml(location.name)}</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>`;
  const blob = new Blob([xml], { type: "application/gpx+xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `traildog-${slug(location.name)}.gpx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeXml(s) {
  return String(s || "").replace(/[<>&'"]/g, (c) => ({"<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;"}[c]));
}
function slug(s) {
  return String(s || "trail").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
